import { useEffect, useMemo, useRef, useState } from "react"
import Link from "next/link"

import { Button } from "@coval/ui"

import { sortEvidenceNewestFirst } from "../utils/evidenceOrder"
import type { InvestigationEvidenceItem } from "../utils/investigationFindings"

// A recurring finding can carry hundreds of quotes. The newest few show whether
// it is still happening; the rest open in steps on request.
const INITIAL_VISIBLE_EVIDENCE = 5
const EVIDENCE_REVEAL_STEP = 20

interface FindingEvidenceListProps {
  evidenceCount: number
  items: InvestigationEvidenceItem[]
  truncated: boolean
}

export function FindingEvidenceList({
  evidenceCount,
  items,
  truncated,
}: FindingEvidenceListProps) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_EVIDENCE)
  const [revealedFrom, setRevealedFrom] = useState<number | null>(null)
  const firstRevealedRef = useRef<HTMLLIElement>(null)

  const ordered = useMemo(() => sortEvidenceNewestFirst(items), [items])
  const shown = ordered.slice(0, visibleCount)
  const remaining = ordered.length - shown.length
  const total = Math.max(evidenceCount, ordered.length)

  // Focus lands on the first quote a reveal added, so keyboard and screen
  // reader users read on from where the list grew instead of being stranded on
  // a button that the last reveal removes.
  useEffect(() => {
    if (revealedFrom !== null) {
      firstRevealedRef.current?.focus()
    }
  }, [revealedFrom])

  const revealMore = () => {
    setRevealedFrom(shown.length)
    setVisibleCount(shown.length + EVIDENCE_REVEAL_STEP)
  }

  return (
    <>
      <ul className="mt-2 space-y-3">
        {ordered.length === 0 && !truncated && (
          <li className="text-sm text-coval-text-muted">
            The evidence for this finding has expired from retention.
          </li>
        )}
        {shown.map((item, index) => (
          <li
            key={item.evidenceId}
            ref={index === revealedFrom ? firstRevealedRef : undefined}
            tabIndex={index === revealedFrom ? -1 : undefined}
            className="space-y-1 border-l-2 border-coval-border pl-3"
          >
            <blockquote className="text-sm leading-5 text-coval-text">
              &ldquo;{item.quote}&rdquo;
            </blockquote>
            <p className="text-xs text-coval-text-muted">
              {item.speaker} · turn {item.turnIndex}
              {item.metricVerdict ? (
                <> · judge said {item.metricVerdict}</>
              ) : null}
              {item.href ? (
                <>
                  {" · "}
                  <Link
                    href={item.href}
                    className="text-coval-info-text hover:underline"
                  >
                    {item.sourceLabel}
                  </Link>
                </>
              ) : null}
            </p>
          </li>
        ))}
      </ul>
      {shown.length > 0 && shown.length < total ? (
        <div className="flex flex-wrap items-center gap-3 px-1 pt-2">
          <p className="text-sm text-coval-text-muted">
            Showing {shown.length} of {total} evidence items.
          </p>
          {remaining > 0 ? (
            <Button type="button" variant="secondary" onClick={revealMore}>
              Show {Math.min(EVIDENCE_REVEAL_STEP, remaining)} more
            </Button>
          ) : null}
        </div>
      ) : null}
      {truncated ? (
        <p className="px-1 pt-2 text-sm text-coval-text-muted">
          This evidence list reached its page limit; more items may not be
          shown.
        </p>
      ) : null}
    </>
  )
}
